import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import List from "../../common/list";
import * as ListCellRenders from "../../common/list/ListCellRenders";
import TextInput from "../../common/fields/TextInput";
import { useListSearch } from "../../common/hooks";
import * as CertificateBatchConstants from "../../../constants/CertificateBatchConstants";
import { formatDateTime } from "../../../helpers/ScheduleSummary";
import {
  isPending,
  statusLabelId,
  statusSeverity,
} from "../../../helpers/CertificateBatchHelper";
import {
  certificateBatchListRequest,
  certificateBatchSetQuery,
} from "../../../store/slices/certificateBatchSlice";
import downloadBatch, { downloadFailureMessageId } from "./downloadBatch";
import CertificateBatchDialog from "./CertificateBatchDialog";

// One empty list for every render, so the rows below stay the same until
// the batches change.
const NO_BATCHES = [];

export const CertificateBatchList = ({
  labels,
  batches = NO_BATCHES,
  totalCount = 0,
  query,
  requesting = false,
  creating = false,
  createError = null,
  loadBatches,
  setQuery,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [downloadingId, setDownloadingId] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const wasCreating = useRef(creating);
  // The row menu keeps the download it was drawn with (see rows below), so
  // a failed download reloads the list with the query of the latest render.
  const queryRef = useRef(query);
  queryRef.current = query;
  // The search (training name) is part of the list query, sent as
  // namePattern from page 1 once typing settles.
  const [namePattern, setNamePattern] = useListSearch(
    query.namePattern,
    (value) => setQuery({ ...query, namePattern: value, pageNo: 1 })
  );

  useEffect(() => {
    loadBatches(query);
  }, [query, loadBatches]);

  // A batch started with Download certificates: list it once the API has
  // created it (a refused one is not listed, so nothing to reload), or once
  // the API has marked it Failed because it could not start.
  useEffect(() => {
    if (
      wasCreating.current &&
      !creating &&
      (!createError ||
        createError === CertificateBatchConstants.CERTIFICATE_BATCH_NOT_STARTED)
    ) {
      loadBatches(query);
    }
    wasCreating.current = creating;
  }, [creating, createError, query, loadBatches]);

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const download = async (batch) => {
    setDownloadingId(batch.id);
    try {
      await downloadBatch(batch.id);
    } catch (error) {
      // The API's own reason when it gives one (the file is gone from
      // storage), otherwise the general message.
      const messageId =
        (await downloadFailureMessageId(error)) ||
        "certificate_batch_download_failed";
      toast.error(intl.formatMessage({ id: messageId }));
      loadBatches(queryRef.current);
    } finally {
      setDownloadingId(null);
    }
  };

  // Why Download cannot be used (an i18n id): the file is not ready, could
  // not be made, has expired or is downloading.
  const downloadUnavailableReason = (batch) => {
    if (batch.downloading) {
      return "certificate_batch_downloading";
    }
    if (isPending(batch)) {
      return "certificate_batch_download_pending";
    }
    return batch.status ===
      CertificateBatchConstants.CERTIFICATE_BATCH_STATUS_FAILED
      ? "certificate_batch_download_not_prepared"
      : "certificate_batch_download_unavailable";
  };

  // The row menu (common/moreActions). A batch has no page of its own, so a
  // click on the row opens nothing.
  const rowActions = [
    {
      label: "certificate_batch_download",
      icon: "pi-download",
      isAllowed: true,
      additionalCheckForAllowed: (batch) =>
        Boolean(batch.canDownload) && !batch.downloading,
      additionalCheckNotAllowedMessage: downloadUnavailableReason,
      action: download,
    },
  ];

  // The table redraws a row's menu only when the row's data changes
  // (PrimeReact memoizes body cells), so the download in progress is put on
  // the row. The rows are built again only when the batches or the download
  // change: a key typed in the search box renders this screen, and new row
  // objects made the table compare every cell again (code review phase 2,
  // finding 11).
  const rows = useMemo(
    () =>
      batches.map((batch) => ({
        ...batch,
        downloading: downloadingId === batch.id,
      })),
    [batches, downloadingId]
  );

  const columns = [
    {
      field: "itemName",
      header: "certificate_batch_item",
      // One item: its name and type. Several items or All training: the
      // batch name the API gives ("3 training items", "All training").
      body: (batch) => (
        <div>
          <div>{batch.itemName}</div>
          {(CertificateBatchConstants.CERTIFICATE_ITEM_TYPE_LABELS[
            batch.itemType
          ] && (
            <small>
              {intl.formatMessage({
                id: CertificateBatchConstants.CERTIFICATE_ITEM_TYPE_LABELS[
                  batch.itemType
                ],
              })}
            </small>
          )) ||
            null}
        </div>
      ),
    },
    {
      field: "createdAt",
      header: "certificate_batch_requested_at",
      body: (batch) => formatDateTime(batch.createdAt, intl),
    },
    {
      field: "certificateCount",
      header: "certificate_batch_certificates",
      body: (batch) => (
        <div>
          <div>{intl.formatNumber(batch.certificateCount || 0)}</div>
          {(batch.skippedCount > 0 && (
            <small>
              {intl.formatMessage(
                { id: "certificate_batch_left_out" },
                { count: batch.skippedCount }
              )}
            </small>
          )) ||
            null}
        </div>
      ),
    },
    {
      field: "status",
      header: "status",
      body: (batch) => (
        <div className="flex flex-column align-items-start gap-1">
          <Tag
            value={intl.formatMessage({ id: statusLabelId(batch) })}
            severity={statusSeverity(batch)}
          />
          {(batch.failureReason && <small>{batch.failureReason}</small>) ||
            null}
        </div>
      ),
    },
    {
      field: "fileExpiresAtUtc",
      header: "certificate_batch_expires",
      body: (batch) =>
        batch.canDownload ? formatDateTime(batch.fileExpiresAtUtc, intl) : "-",
    },
    {
      field: "actions",
      headerIcon: "pi-cog",
      align: "center",
      body: ListCellRenders.OtherActions(rowActions),
    },
  ];

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "certificate_batch_downloads" })}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              icon="pi pi-verified"
              label={intl.formatMessage({
                id: "certificate_batch_download_certificates",
              })}
              size="small"
              onClick={() => setDialogVisible(true)}
            />
            <Button
              type="button"
              icon="pi pi-refresh"
              label={intl.formatMessage({ id: "certificate_batch_refresh" })}
              outlined
              size="small"
              onClick={() => loadBatches(query)}
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_certificate_batches",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
          </div>
          {(!requesting && !query.namePattern && batches.length === 0 && (
            <p>{intl.formatMessage({ id: "certificate_batch_none" })}</p>
          )) || (
            <List
              columns={columns}
              data={rows}
              loading={requesting}
              selectionMode={null}
              pageQuery={query}
              totalRecords={totalCount}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      </div>
      <CertificateBatchDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
      />
    </div>
  );
};

CertificateBatchList.propTypes = {
  labels: PropTypes.array,
  batches: PropTypes.array,
  totalCount: PropTypes.number,
  query: PropTypes.object.isRequired,
  requesting: PropTypes.bool,
  creating: PropTypes.bool,
  createError: PropTypes.string,
  loadBatches: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  batches: state.certificateBatches.list,
  totalCount: state.certificateBatches.totalCount,
  query: state.certificateBatches.query,
  requesting: state.certificateBatches.requesting,
  creating: state.certificateBatches.creating,
  createError: state.certificateBatches.createError,
});

const mapDispatchToProps = (dispatch) => ({
  loadBatches: (query) => dispatch(certificateBatchListRequest(query)),
  setQuery: (query) => dispatch(certificateBatchSetQuery(query)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CertificateBatchList);
